import { Button } from "@mui/material";
import {
	ClickHere,
	CreateYourItemSubtitleText,
	DraftANewOrderText,
	FindOriginalOrderText,
	FindOriginalText,
	ManualOrderEntryText,
	NewOrderMarketingMaterialsBannerLinkText,
	NewOrderMarketingMaterialsBannerPrefixText,
	ReplacementOrAddOnText,
	ToAccessText
} from "constants/text";
import {
	DraftNewOrderHeader,
	GridChildTwoContainer,
	GridChildTwoStyles,
	NewOrdersContentContainer,
	NewOrdersHeaderGrid,
	OldExperienceContainer,
	OrderOrAddOnText,
	UnsetSecondaryCopy
} from "components/NewOrders/NewOrders.styles";
import CrossNavigationBanner from "components/Common/CrossNavigationBanner";
import { getMarketingMaterialsLandingUrl } from "features/MarketingMaterials/utils/getMarketingMaterialsLandingUrl.ts";
import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import { useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import NewOrdersHeader from "components/NewOrders/NewOrdersHeader/NewOrdersHeader";
import SaveDraftTable from "components/NewOrders/SaveDraft/SaveDraftTable/SaveDraftTable.tsx";
import AddGlobalAttributes from "components/NewOrders/AddGlobalAttributes/AddGlobalAttributes";
import GlobalFormProvider from "components/NewOrders/AddGlobalAttributes/forms/GlobalFormProvider";
import ImportCSV from "components/NewOrders/ImportCSV/ImportCSV";
import { newOrderActions } from "features/reducers/newOrder/newOrder";
import { useContext, useEffect } from "react";
import {
	AddOnBoxButtonText,
	AddOnBoxText,
	AddOnBoxTitle,
	CustomColorBoxButtonText,
	CustomColorBoxText,
	CustomColorBoxTitle,
	LowesAddOnBoxTitle,
	NewOrderBoxText,
	NewOrderBoxTitle,
	NewQuoteBoxText,
	NewQuoteTitleText,
	StartQuoteButtonText
} from "components/NewOrders/constants";
import { salesforceBaseURL } from "constants/endpoints.ts";
import MuiLink from "@mui/material/Link";
import NewOrderLandingPageBox, {
	NewOrderLandingPageBoxProps
} from "components/NewOrders/NewOrderLandingPageBox/NewOrderLandingPageBox";
import { NewOrderLandingPageBoxes } from "components/NewOrders/NewOrderLandingPageBox/NewOrderLandingPageBox.styles";
import { DialogTracking } from "components/Dialog/components/DialogTracking.ts";
import ConfigurationsContext from "context/configurations/ConfigurationsContext.ts";
import { useDialog } from "hooks/useDialog.ts";
import { useGetUserDetailsQuery } from "features/api/userApi.ts";
import { UserChannel, UserRole } from "types/declarations/enums.ts";
import { Page } from "components/Common/Page";

const NewOrderPage = () => {
	const { configurationsData } = useContext(ConfigurationsContext);
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const { openDialog, dialogStatus } = useDialog();
	const dialogStatusManualOrderEntry = dialogStatus === DialogTracking.MANUAL_ORDER_ENTRY;
	const dialogStatusCustomOrderEntry = dialogStatus === DialogTracking.CUSTOM_COLOR_ENTRY;
	const { data: userDetails } = useGetUserDetailsQuery();
	const isLowesUser = userDetails?.userChannel === UserChannel.LOWES;
	const isInternalDesigner = userDetails?.role === UserRole.INTERNAL_DESIGNER;

	const handleFindOriginalOrder = () => {
		navigate("/orders");
	};

	useEffect(() => {
		dispatch(newOrderActions.clearNewOrder());
	}, [dispatch]);

	const isShowingNewLandingPage = configurationsData?.featureFlags?.customColorEnabled;
	const isMarketingMaterialsBannerEnabled =
		configurationsData?.featureFlags?.marketingMaterialsBannerEnabled === true;
	const shouldShowMarketingMaterialsBanner = isMarketingMaterialsBannerEnabled && !isLowesUser;

	const landingPageBoxes: NewOrderLandingPageBoxProps[] = [
		{
			title: NewOrderBoxTitle,
			text: NewOrderBoxText,
			imgSrc: "assets/new_order_page_boxes/NewOrderPageBox_NewOrder.svg",
			buttonText: NewOrderBoxTitle,
			buttonVariant: "contained",
			buttonDataTestId: "new-order-landing-page-new-order-button",
			onClick: () => {
				openDialog({ dialogStatus: DialogTracking.BUILD_ORDER_OPTIONS });
			}
		},
		{
			title: AddOnBoxTitle,
			text: AddOnBoxText,
			imgSrc: "assets/new_order_page_boxes/NewOrderPageBox_FindOrder.svg",
			buttonText: AddOnBoxButtonText,
			buttonVariant: "outlined",
			buttonDataTestId: "new-order-landing-page-find-order-button",
			onClick: handleFindOriginalOrder
		},
		{
			title: CustomColorBoxTitle,
			text: CustomColorBoxText,
			imgSrc: "assets/new_order_page_boxes/NewOrderPageBox_CustomColor.svg",
			buttonText: CustomColorBoxButtonText,
			buttonVariant: "outlined",
			buttonDataTestId: "new-order-landing-page-custom-color-button",
			onClick: () => openDialog({ dialogStatus: DialogTracking.CUSTOM_COLOR_ENTRY })
		}
	];

	const lowesLandingPageBoxes: NewOrderLandingPageBoxProps[] = [
		{
			title: LowesAddOnBoxTitle,
			text: AddOnBoxText,
			imgSrc: "assets/new_order_page_boxes/NewOrderPageBox_FindOrder.svg",
			buttonText: AddOnBoxButtonText,
			buttonVariant: "outlined",
			buttonDataTestId: "new-order-landing-page-find-order-button",
			onClick: handleFindOriginalOrder
		}
	];

	const internalDesignerLandingPageBoxes: NewOrderLandingPageBoxProps[] = [
		{
			title: NewQuoteTitleText,
			text: NewQuoteBoxText,
			imgSrc: "assets/new_order_page_boxes/NewOrderPageBox_FindOrder.svg",
			buttonText: StartQuoteButtonText,
			buttonVariant: "contained",
			buttonDataTestId: "new-order-landing-page-find-order-button",
			onClick: () => {
				openDialog({ dialogStatus: DialogTracking.BUILD_ORDER_OPTIONS });
			}
		}
	];

	const showLandingPageBoxes = () => {
		if (isLowesUser) {
			return (
				<NewOrderLandingPageBoxes>
					{lowesLandingPageBoxes.map((box) => {
						return (
							<NewOrderLandingPageBox
								key={box.title}
								title={box.title}
								text={box.text}
								imgSrc={box.imgSrc}
								buttonText={box.buttonText}
								buttonVariant={box.buttonVariant}
								buttonDataTestId={box.buttonDataTestId}
								onClick={box.onClick}
							/>
						);
					})}
				</NewOrderLandingPageBoxes>
			);
		} else if (isInternalDesigner) {
			return (
				<NewOrderLandingPageBoxes>
					{internalDesignerLandingPageBoxes.map((box) => {
						return (
							<NewOrderLandingPageBox
								key={box.title}
								title={box.title}
								text={box.text}
								imgSrc={box.imgSrc}
								buttonText={box.buttonText}
								buttonVariant={box.buttonVariant}
								buttonDataTestId={box.buttonDataTestId}
								onClick={box.onClick}
							/>
						);
					})}
				</NewOrderLandingPageBoxes>
			);
		} else {
			return (
				<NewOrderLandingPageBoxes>
					{landingPageBoxes.map((box) => {
						if (box.text === CustomColorBoxText && !configurationsData?.featureFlags?.customColorEnabled)
							return <></>;
						return (
							<NewOrderLandingPageBox
								key={box.title}
								title={box.title}
								text={box.text}
								imgSrc={box.imgSrc}
								buttonText={box.buttonText}
								buttonVariant={box.buttonVariant}
								buttonDataTestId={box.buttonDataTestId}
								onClick={box.onClick}
							/>
						);
					})}
				</NewOrderLandingPageBoxes>
			);
		}
	};

	return (
		<Page variant="orders">
			<NewOrdersHeader
				includeDetails={false}
				includeStepper={false}
				variant="title"
			/>
			<NewOrdersContentContainer $inset>
				{shouldShowMarketingMaterialsBanner && (
					<CrossNavigationBanner
						to={getMarketingMaterialsLandingUrl()}
						prefixText={NewOrderMarketingMaterialsBannerPrefixText}
						actionText={NewOrderMarketingMaterialsBannerLinkText}
						dataTestId="new-order-marketing-materials-banner"
					/>
				)}
				{isShowingNewLandingPage || isLowesUser ? (
					showLandingPageBoxes()
				) : (
					<>
						<DraftNewOrderHeader>{DraftANewOrderText}</DraftNewOrderHeader>
						<OldExperienceContainer>
							<span>{ToAccessText}</span>
							<MuiLink
								href={`${salesforceBaseURL.replace(/\/$/, "")}/sf/place-an-order`}
								target="_blank"
								rel="noreferrer"
								data-testid="legacy-order-experience"
							>
								{ClickHere}
							</MuiLink>
						</OldExperienceContainer>
						<NewOrdersHeaderGrid>
							<ImportCSV isNewQuoteOrder={isInternalDesigner} />
							<GridChildTwoStyles>
								<GridChildTwoContainer>
									<OrderOrAddOnText>{ReplacementOrAddOnText}</OrderOrAddOnText>
									<Button
										onClick={handleFindOriginalOrder}
										variant="outlined"
										startIcon={
											<ArrowOutwardIcon
												fontSize="small"
												sx={{ marginBottom: "2px" }}
											/>
										}
										data-id="new-order-find-original-order-button"
										sx={{
											display: "flex",
											alignItems: "center",
											height: "fit-content",
											width: "max-content",
											marginLeft: "auto"
										}}
									>
										{FindOriginalOrderText}
									</Button>
									<UnsetSecondaryCopy>{FindOriginalText}</UnsetSecondaryCopy>
								</GridChildTwoContainer>
								<div style={{ height: 20, background: "#f7f7f7" }} />
								<GridChildTwoContainer>
									<OrderOrAddOnText>{ManualOrderEntryText}</OrderOrAddOnText>
									<GlobalFormProvider>
										<AddGlobalAttributes
											showAddItem
											isCustomColor={false}
										/>
									</GlobalFormProvider>
									<UnsetSecondaryCopy>{CreateYourItemSubtitleText}</UnsetSecondaryCopy>
								</GridChildTwoContainer>
							</GridChildTwoStyles>
						</NewOrdersHeaderGrid>
					</>
				)}
				<SaveDraftTable
					isLowesUser={isLowesUser}
					isInternalDesigner={isInternalDesigner}
				/>
			</NewOrdersContentContainer>
			{(dialogStatusCustomOrderEntry || dialogStatusManualOrderEntry) && isShowingNewLandingPage && (
				<GlobalFormProvider>
					<AddGlobalAttributes
						showAddItem
						isLandingPage
						isCustomColor={!dialogStatusManualOrderEntry}
						isNewOrderQuote={isInternalDesigner}
					/>
				</GlobalFormProvider>
			)}
		</Page>
	);
};

export default NewOrderPage;
